import { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import type { AnswerCta } from '../components/AnswerCard';
import { ChatThread, type DisplayMessage } from '../components/ChatThread';
import { HelpEmptyState } from '../components/HelpEmptyState';
import { HelpInput } from '../components/HelpInput';
import { SuggestionChips } from '../components/SuggestionChips';
import type { HelpStore } from '../helpStore';
import { renderAnswerBlocks } from '../lib/answerBlocks';
import { buildHelpMailto } from '../lib/contactMailto';
import { resolveCtaTarget } from '../lib/resolveContext';
import type { HelpFaq, HelpMessage } from '../lib/types';
import { useHelp } from '../useHelp';
import { ContactUsBar } from './ContactUsBar';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-09-17` → `17 Sep 2026`. */
export function formatUpdated(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  const [, year, month, day] = match;
  return `${Number(day)} ${MONTHS[Number(month) - 1] ?? month} ${year}`;
}

function ctaFor(faq: HelpFaq, store: HelpStore): AnswerCta | null {
  const cta = faq.cta;
  if (!cta) return null;
  if (cta.action === 'navigate' && (!cta.target || resolveCtaTarget(cta.target, store.routeParams) === null)) return null;
  return { label: cta.label, onClick: () => store.runCta(cta) };
}

function FallbackExtras({ suggestions, mailto, onSelect }: { suggestions: HelpFaq[]; mailto: string; onSelect: (id: string) => void }) {
  return (
    <>
      {suggestions.length > 0 && (
        <SuggestionChips
          label={`${suggestions.length === 1 ? 'One' : 'Two'} I can answer`}
          chips={suggestions.map((faq) => ({ id: faq.id, label: faq.short_label ?? faq.question }))}
          onSelect={onSelect}
        />
      )}
      <div className="flex flex-col gap-[7px] border-t border-[#e5e7eb] pt-[15px]">
        <span className="text-[13px] leading-[1.55] text-[#717182]">The DVA team can pick this up from here.</span>
        <a
          href={mailto}
          className="flex items-center gap-1.5 self-start border-b border-dvaNavy pb-0.5 text-[13px] font-bold text-dvaNavy no-underline hover:border-dvaOrange focus-visible:rounded focus-visible:ring-[3px] focus-visible:ring-dvaOrange/35 focus-visible:outline-none"
        >
          Contact the DVA team
          <ArrowRight className="size-3.5" strokeWidth={2.2} aria-hidden="true" />
        </a>
      </div>
    </>
  );
}

function toDisplay(message: HelpMessage, store: HelpStore): DisplayMessage {
  if (message.role === 'user') return { id: message.id, role: 'user', text: message.text };

  const paragraphs = renderAnswerBlocks(message.text);
  if (message.variant === 'fallback') {
    const suggestions = (message.suggestedFaqIds ?? []).map(store.findFaq).filter((faq): faq is HelpFaq => Boolean(faq));
    const mailto = buildHelpMailto({ email: store.contactEmail, contextLabel: store.activeContext.label, messages: store.messages });
    return {
      id: message.id,
      role: 'assistant',
      variant: 'fallback',
      paragraphs,
      extras: <FallbackExtras suggestions={suggestions} mailto={mailto} onSelect={store.askFaq} />,
    };
  }

  const faq = message.sourceFaqId ? store.findFaq(message.sourceFaqId) : undefined;
  return {
    id: message.id,
    role: 'assistant',
    variant: 'answer',
    paragraphs,
    callout: faq?.callout ?? null,
    cta: faq ? ctaFor(faq, store) : null,
    source: faq ? { label: 'From the DVA help guide', updatedAt: formatUpdated(faq.updated_at) } : null,
  };
}

/** Right pane: guide card or thread, the composer, and the quiet contact line. */
export function ChatPane() {
  const store = useHelp();
  const { activeContext, guide, messages, pending, draft, setDraft, sendMessage, askFaq, contactEmail } = store;

  const displayMessages = useMemo(() => messages.map((message) => toDisplay(message, store)), [messages, store]);

  return (
    <>
      <ChatThread messages={displayMessages} pending={pending} emptyState={<HelpEmptyState guide={guide} onAsk={askFaq} />} />
      <HelpInput
        contextLabel={activeContext.label}
        value={draft}
        onChange={setDraft}
        onSubmit={sendMessage}
        status={pending ? 'sending' : 'idle'}
      />
      <ContactUsBar contextLabel={activeContext.label} messages={messages} email={contactEmail} />
    </>
  );
}
